import React, { useState, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FaArrowUp, FaArrowDown, FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import ShareButton from './ShareButton';
import { buildPatternDetailUrl as buildPatternDetailUrlBase, SIGNUP_URL } from '../utils/links';
import { useAuthUrl } from '../utils/authRedirect';
import { useFundamentals } from '../hooks/useFundamentals';
import './PatternCard.css';

// Build pattern detail URL preserving current search params for back navigation
function buildPatternDetailUrl(pattern: PatternMatch, searchParams: URLSearchParams, lookbackDays?: number) {
  return buildPatternDetailUrlBase({
    ticker: pattern.ticker,
    patternType: pattern.pattern_type,
    startDate: pattern.start_date,
    sentiment: pattern.sentiment ?? undefined,
    window: pattern.window || undefined,
    view: searchParams.get('view') ?? undefined,
    types: searchParams.get('types') ?? undefined,
    lookbackDays: lookbackDays || undefined,
  });
}

interface PatternCardProps {
  pattern: PatternMatch;
  showNewBadge?: boolean;
  compact?: boolean;
  isObfuscated?: boolean;
  lookbackDays?: number;
}

export function PatternCard({ pattern, showNewBadge = false, compact = false, isObfuscated = false, lookbackDays }: PatternCardProps) {
  const authUrlFor = useAuthUrl();
  const [searchParams] = useSearchParams();
  const {
    pattern_type,
    pattern_display_name,
    ticker,
    window,
    start_date,
    end_date,
    confidence,
    sentiment,
    price_data,
  } = pattern;

  // Calculate actual pattern length from price data
  const patternLength = price_data ? price_data.length : window;

  // Format confidence as percentage
  const confidencePct = (confidence * 100).toFixed(1);

  // Get sentiment indicator
  const getSentimentIcon = () => {
    if (sentiment === 'bullish') {
      return <FaArrowUp className="sentiment-icon bullish" />;
    } else if (sentiment === 'bearish') {
      return <FaArrowDown className="sentiment-icon bearish" />;
    }
    return null;
  };

  // Get sentiment class for styling
  const sentimentClass = sentiment ? `pattern-card-${sentiment}` : '';

  // Pattern detail URL (include sentiment and view for back navigation)
  const patternDetailUrl = buildPatternDetailUrl(pattern, searchParams, lookbackDays);

  if (compact) {
    return (
      <Link to={isObfuscated ? authUrlFor(SIGNUP_URL) : patternDetailUrl} className="pattern-card-link">
        <div className={`pattern-card-compact ${sentimentClass}`}>
          <div className="pattern-header">
            <span className="pattern-ticker" style={isObfuscated ? {filter: 'blur(5px)', userSelect: 'none'} : {}}>
              {ticker}
            </span>
            {isObfuscated && <span style={{marginLeft: 4}}>🔒</span>}
            {showNewBadge && <span className="new-badge">NEW</span>}
            {getSentimentIcon()}
          </div>
          <div className="pattern-name">{pattern_display_name || pattern_type}</div>
          <div className="pattern-confidence">
            <div className="confidence-bar">
              <div
                className="confidence-fill"
                style={{ width: `${confidencePct}%` }}
              ></div>
            </div>
            <span className="confidence-value">{confidencePct}%</span>
          </div>
          <div className="pattern-window">{patternLength}d</div>
        </div>
      </Link>
    );
  }

  return (
    <Link to={isObfuscated ? authUrlFor(SIGNUP_URL) : patternDetailUrl} className="pattern-card-link">
      <div className={`pattern-card ${sentimentClass}`}>
        {showNewBadge && <div className="new-badge-top">NEW</div>}

        <div className="pattern-card-header">
          <div className="pattern-title">
            <h3>{pattern_display_name || pattern_type}</h3>
            {getSentimentIcon()}
          </div>
          <div className="pattern-ticker-large" style={isObfuscated ? {filter: 'blur(6px)', userSelect: 'none'} : {}}>
            {ticker}
          </div>
          {isObfuscated && <div style={{fontSize: '0.75rem', marginTop: 2}}>🔒 Sign up to reveal</div>}
        </div>

        <div className="pattern-card-body">
          <div className="pattern-stat">
            <span className="stat-label">Confidence</span>
            <span className="stat-value">{confidencePct}%</span>
            <div className="confidence-bar-large">
              <div
                className="confidence-fill"
                style={{ width: `${confidencePct}%` }}
              ></div>
            </div>
          </div>

          <div className="pattern-details">
            <div className="pattern-detail">
              <span className="detail-label">Length</span>
              <span className="detail-value">{patternLength} days</span>
            </div>
            <div className="pattern-detail">
              <span className="detail-label">Period</span>
              <span className="detail-value">
                {start_date} to {end_date}
              </span>
            </div>
          </div>

          {!isObfuscated && (
            <div className="mt-2 text-end">
              <ShareButton
                color="secondary"
                outline
                size="sm"
                title={`${ticker} — ${pattern_display_name || pattern_type} | DejaChart`}
                path={`/api/share/pattern/${ticker}?type=${pattern_type}&window=${window}&start=${start_date}`}
              />
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}

// Component for displaying a grid of patterns
interface PatternGridProps {
  patterns: PatternMatch[];
  showNewBadge?: boolean;
  compact?: boolean;
  isAnonymous?: boolean;
  lookbackDays?: number;
}

export function PatternGrid({ patterns, showNewBadge = false, compact = false, lookbackDays }: PatternGridProps) {
  if (patterns.length === 0) {
    return <div className="no-patterns">No patterns found</div>;
  }

  return (
    <div className={`pattern-grid ${compact ? 'compact' : ''}`}>
      {patterns.map((pattern, index) => (
        <PatternCard
          key={`${pattern.ticker}-${pattern.pattern_type}-${pattern.start_date}-${index}`}
          pattern={pattern}
          showNewBadge={showNewBadge}
          compact={compact}
          isObfuscated={pattern.ticker === '?????'}
          lookbackDays={lookbackDays}
        />
      ))}
    </div>
  );
}

// Component for displaying patterns as a table
type SortColumn = 'ticker' | 'pattern_type' | 'sector' | 'confidence' | 'period' | null;
type SortDirection = 'asc' | 'desc';

interface PatternTableProps {
  patterns: PatternMatch[];
  showNewBadge?: boolean;
  isAnonymous?: boolean;
  lookbackDays?: number;
}

export function PatternTable({ patterns, showNewBadge = false, lookbackDays }: PatternTableProps) {
  const authUrlFor = useAuthUrl();
  const [searchParams] = useSearchParams();
  const getFundamentals = useFundamentals();
  const [sortCol, setSortCol] = useState<SortColumn>(null);
  const [sortDir, setSortDir] = useState<SortDirection>('asc');

  const handleSort = (col: SortColumn) => {
    if (sortCol === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(col);
      setSortDir(col === 'confidence' ? 'desc' : 'asc');
    }
  };

  const sortedPatterns = useMemo(() => {
    if (!sortCol) return patterns;
    return [...patterns].sort((a, b) => {
      let cmp = 0;
      if (sortCol === 'ticker') {
        cmp = a.ticker.localeCompare(b.ticker);
      } else if (sortCol === 'pattern_type') {
        cmp = (a.pattern_display_name || a.pattern_type).localeCompare(b.pattern_display_name || b.pattern_type);
      } else if (sortCol === 'sector') {
        cmp = (getFundamentals(a.ticker)?.sector || '').localeCompare(getFundamentals(b.ticker)?.sector || '');
      } else if (sortCol === 'confidence') {
        cmp = a.confidence - b.confidence;
      } else {
        cmp = a.start_date.localeCompare(b.start_date);
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    // getFundamentals is included because its underlying cache loads asynchronously —
    // without it, a sector sort applied before the fetch resolves would never re-run.
  }, [patterns, sortCol, sortDir, getFundamentals]);

  const SortIcon = ({ col }: { col: SortColumn }) => {
    if (sortCol !== col) return <FaSort className="sort-icon sort-icon-inactive" />;
    return sortDir === 'asc'
      ? <FaSortUp className="sort-icon sort-icon-active" />
      : <FaSortDown className="sort-icon sort-icon-active" />;
  };

  if (patterns.length === 0) {
    return <div className="no-patterns">No patterns found</div>;
  }

  return (
    <div className="pattern-table-container">
      <table className="pattern-table">
        <thead>
          <tr>
            <th className="sortable-th" onClick={() => handleSort('ticker')}>
              Ticker <SortIcon col="ticker" />
            </th>
            <th className="sortable-th" onClick={() => handleSort('sector')}>
              Sector <SortIcon col="sector" />
            </th>
            <th className="sortable-th" onClick={() => handleSort('pattern_type')}>
              Pattern <SortIcon col="pattern_type" />
            </th>
            <th>Sentiment</th>
            <th className="sortable-th" onClick={() => handleSort('confidence')}>
              Confidence <SortIcon col="confidence" />
            </th>
            <th>Length</th>
            <th className="sortable-th" onClick={() => handleSort('period')}>
              Period <SortIcon col="period" />
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedPatterns.map((pattern, index) => {
            const isObfuscated = pattern.ticker === '?????';
            const patternDetailUrl = buildPatternDetailUrl(pattern, searchParams, lookbackDays);
            const confidencePct = (pattern.confidence * 100).toFixed(1);
            const patternLength = pattern.price_data ? pattern.price_data.length : pattern.window;

            return (
              <tr key={`${pattern.ticker}-${pattern.pattern_type}-${pattern.start_date}-${index}`}>
                <td className="ticker-cell">
                  {isObfuscated ? (
                    <Link to={authUrlFor(SIGNUP_URL)} className="pattern-table-link">
                      <span className="ticker-text" style={{filter: 'blur(5px)', userSelect: 'none'}}>{pattern.ticker}</span>
                      {' '}🔒
                    </Link>
                  ) : (
                    <Link to={patternDetailUrl} className="pattern-table-link">
                      <span className="ticker-text">{pattern.ticker}</span>
                      {showNewBadge && <span className="new-badge-small">NEW</span>}
                    </Link>
                  )}
                </td>
                <td>{getFundamentals(pattern.ticker)?.sector || '—'}</td>
                <td>
                  <Link to={isObfuscated ? authUrlFor(SIGNUP_URL) : patternDetailUrl} className="pattern-table-link">
                    {pattern.pattern_display_name || pattern.pattern_type}
                  </Link>
                </td>
                <td>
                  <span className={`sentiment-badge sentiment-${pattern.sentiment}`}>
                    {pattern.sentiment === 'bullish' && <FaArrowUp />}
                    {pattern.sentiment === 'bearish' && <FaArrowDown />}
                    {pattern.sentiment}
                  </span>
                </td>
                <td>
                  <div className="confidence-cell">
                    <div className="confidence-bar-small">
                      <div
                        className="confidence-fill"
                        style={{ width: `${confidencePct}%` }}
                      ></div>
                    </div>
                    <span className="confidence-text">{confidencePct}%</span>
                  </div>
                </td>
                <td>{patternLength}d</td>
                <td className="period-cell">
                  <Link to={isObfuscated ? authUrlFor(SIGNUP_URL) : patternDetailUrl} className="pattern-table-link">
                    {pattern.start_date} → {pattern.end_date}
                  </Link>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
